import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { User } from 'types/interfaces/user/User';

import { PopularUser } from 'api/AuthApi';

interface IFeatureFlags {
  isLimitTimeOfferEnabled: boolean;
  isAdsEnabled: boolean;
  isNativeAdsEnabled: boolean;
  isCanShowRetentionPopup: boolean;
  isCanShowGiftCreditsPopup: boolean;
  isPromoteVipPopupEnabled: boolean;
  isPaymentFormIntentEnabled: boolean;
  isComplianceStatementEnabled: boolean;
  isStaticPopularUsersPhotoEnabled: boolean;
  isCarouselPaywallEnabled: boolean;
  isRetentionFunnelsEnabled: boolean;
  isFreeVipEnabled: boolean;
  isAdditionalPriceEnabled: boolean;
  isUpgradeDarkThemeEnabled: boolean;
  isOrganicPartnerId: boolean;
  isNeedAgeVerificationEnabled: boolean;
  isPaywallMonthPackageEnabled: boolean;
  is3dPackageEnabled: boolean;
  isErrorTranslateEnabled: boolean;
  isJoinFeeEnabled: boolean;
  isCreditsPriceOptimizationEnabled: boolean;
  isComericaBankEnabled: boolean;
}

interface ITranslations {
  isEnabled: boolean;
  buildVersion: null | string;
}

interface ICommonState {
  loading: boolean;
  newDialogsCount: number;
  hasNotifications: boolean;
  popups: {
    hasMatch: User | null;
  };
  popularUsers: PopularUser[];
  redirectFromRestrictedPublicPages: boolean;
  prevPageHref: string;
  currentLang: string;
  supportedLangs: string[];
  translations: ITranslations;
  boostExpireDate: null | string;
  featureFlags: IFeatureFlags;
  isLandingValidationSkipEnabled: boolean;
  isLandingAdditionalPriceEnabled: boolean;
  csrfToken: null | string;
}

const initialState: ICommonState = {
  loading: true,
  newDialogsCount: 0,
  hasNotifications: false,
  popups: {
    hasMatch: null,
  },
  popularUsers: [],
  redirectFromRestrictedPublicPages: true,
  prevPageHref: '',
  currentLang: 'en',
  supportedLangs: [],
  translations: {
    isEnabled: false,
    buildVersion: null,
  },
  boostExpireDate: null,
  featureFlags: {
    isLimitTimeOfferEnabled: false,
    isAdsEnabled: false,
    isNativeAdsEnabled: false,
    isCanShowRetentionPopup: false,
    isCanShowGiftCreditsPopup: false,
    isPromoteVipPopupEnabled: false,
    isPaymentFormIntentEnabled: false,
    isComplianceStatementEnabled: false,
    isStaticPopularUsersPhotoEnabled: false,
    isCarouselPaywallEnabled: false,
    isRetentionFunnelsEnabled: false,
    isFreeVipEnabled: false,
    isAdditionalPriceEnabled: false,
    isUpgradeDarkThemeEnabled: false,
    isOrganicPartnerId: true,
    isNeedAgeVerificationEnabled: false,
    isPaywallMonthPackageEnabled: false,
    is3dPackageEnabled: false,
    isErrorTranslateEnabled: false,
    isJoinFeeEnabled: false,
    isCreditsPriceOptimizationEnabled: false,
    isComericaBankEnabled: false,
  },

  isLandingValidationSkipEnabled: true, // ? this is for validation test, remove after test
  isLandingAdditionalPriceEnabled: false,
  csrfToken: null,
};

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setInitState(
      state,
      action: PayloadAction<
        Omit<
          ICommonState,
          | 'redirectFromRestrictedPublicPages'
          | 'prevPageHref'
          | 'isLandingValidationSkipEnabled'
          | 'isLandingAdditionalPriceEnabled'
          | 'csrfToken'
        >
      >
    ) {
      return {
        ...state,
        ...action.payload,
      };
    },

    resetState() {
      return initialState;
    },

    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },

    setHasNotifications(state, action: PayloadAction<boolean>) {
      state.hasNotifications = action.payload;
    },

    setHasMatch(state, action: PayloadAction<User | null>) {
      state.popups.hasMatch = action.payload;
    },

    setRedirectFromRestrictedPublicPages(
      state,
      action: PayloadAction<boolean>
    ) {
      state.redirectFromRestrictedPublicPages = action.payload;
    },

    setPrevPageHref(state, action: PayloadAction<string>) {
      state.prevPageHref = action.payload;
    },

    setCurrentLang(state, action: PayloadAction<string>) {
      state.currentLang = action.payload;
    },

    setBoostEpireDate(state, action: PayloadAction<null | string>) {
      state.boostExpireDate = action.payload;
    },

    updateFeatureFlag(
      state,
      action: PayloadAction<{ flag: keyof IFeatureFlags; value: boolean }>
    ) {
      state.featureFlags[action.payload.flag] = action.payload.value;
    },

    setIsLandingValidationSkipEnabled(state, action: PayloadAction<boolean>) {
      state.isLandingValidationSkipEnabled = action.payload;
    },
    setIsLandingAdditionalPriceEnabled(state, action: PayloadAction<boolean>) {
      state.isLandingAdditionalPriceEnabled = action.payload;
    },
    setCsrfToken(state, action: PayloadAction<null | string>) {
      state.csrfToken = action.payload;
    },
  },
});

export const {
  setInitState,
  resetState,
  setLoading,
  setHasNotifications,
  setHasMatch,
  setRedirectFromRestrictedPublicPages,
  setPrevPageHref,
  setCurrentLang,
  setBoostEpireDate,
  updateFeatureFlag,
  setIsLandingValidationSkipEnabled,
  setIsLandingAdditionalPriceEnabled,
  setCsrfToken,
} = commonSlice.actions;

export const common = commonSlice.reducer;
